import React from 'react';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';

import { PENDING_STOCK_SOURCE_TYPE, STATUS } from '../../../../common/constants';
import { GET_PENDING_STOCK_LIST } from '../../../../common/graphql/pendingStock';
import { Label, Spinner, Text } from '../../../components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

/**
 * Today's pending driver returns, shown on the end-of-day "Return stock" step
 * so the driver acknowledges the actual list, not a generic instruction.
 */
const ReturnStockList = ({ driverId, runDate, noCache = false }) => {
	const styles = useThemedStyles(createStyles);

	const { data, loading, error } = useQuery(GET_PENDING_STOCK_LIST, {
		variables: {
			page: 0,
			perPage: 200,
			sortBy: 'pickOrder',
			sortDirection: -1,
			filter: {
				status: STATUS.PENDING,
				sourceType: PENDING_STOCK_SOURCE_TYPE.REMOVED,
				driver: driverId,
				runDate,
			},
		},
		skip: !driverId || !runDate,
		fetchPolicy: noCache ? 'no-cache' : 'cache-and-network',
		// The list is informative; a failed query must not block the confirmation.
		errorPolicy: 'all',
	});

	const items = data?.getPendingStocks?.items || [];

	if (loading && !items.length) {
		return <Spinner />;
	}

	if (error && !items.length) {
		return (
			<Text style={styles.subtleText}>
				Pending stock could not be loaded. Return your stock as usual and confirm below.
			</Text>
		);
	}

	if (!items.length) {
		return (
			<Text style={styles.subtleText}>
				No pending stock recorded for today. If you have stock in the van, return it as usual and confirm below.
			</Text>
		);
	}

	return (
		<View style={styles.listCard}>
			{
				items.map(item => (
					<View key={item.id} style={styles.row}>
						<Text style={styles.rowText}>{item.product?.name || 'Unknown product'}</Text>
						<Label label={`x${item.quantity}`} primary={true} style={styles.rowLabel} />
					</View>
				))
			}
		</View>
	);
};

const createStyles = (theme) => ({
	subtleText: {
		fontSize: 13,
		color: theme.text2,
		marginTop: 8,
	},
	listCard: {
		backgroundColor: theme.mainBg2,
		borderRadius: 8,
		padding: 10,
		marginTop: 8,
	},
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingVertical: 6,
	},
	rowText: {
		flex: 1,
		fontSize: 13,
		color: theme.text1,
		paddingRight: 8,
	},
	rowLabel: {
		marginBottom: 0,
	},
});

export default ReturnStockList;
